import { useEffect, useState } from "react";

/**
 * Kleiner Batch-Loader für `is_ai_generated`-Flags einer Menge von
 * Media-IDs. Wird von Komponenten benutzt, die kein `SmartMedia`
 * verwenden können (z. B. `BeforeAfterSlider` mit clip-path-Layer),
 * aber trotzdem das KI-Badge konsistent anzeigen müssen.
 *
 * - In-Memory-Cache pro Media-ID (persistiert über den Komponenten-
 *   Lifecycle hinweg, aber nicht über Page-Reload).
 * - Kein Retry bei Netzwerkfehlern — im Zweifel liefert der Hook
 *   einfach `undefined` für die betroffene ID (kein Badge → sicher).
 */

const cache = new Map<string, boolean | undefined>();
const inflight = new Map<string, Promise<boolean | undefined>>();
const subscribers = new Set<() => void>();

function notify() {
    subscribers.forEach((fn) => fn());
}

/**
 * Setzt/aktualisiert das AI-Flag im Modul-Cache, damit alle Hook-Instanzen
 * (SmartMedia, BeforeAfterSlider, AiFlagToggle …) sofort re-rendern.
 * Aufruf typischerweise nach erfolgreichem `PATCH /api/admin/media/{id}`.
 */
export function setAiFlagLocal(mediaId: string, flag: boolean): void {
    cache.set(mediaId, flag);
    notify();
}

async function loadOne(mediaId: string): Promise<boolean | undefined> {
    if (cache.has(mediaId)) return cache.get(mediaId);
    const existing = inflight.get(mediaId);
    if (existing) return existing;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const p = (async () => {
        try {
            const res = await fetch(`${base}/api/media/${mediaId}/info`, { credentials: "omit" });
            if (!res.ok) {
                cache.set(mediaId, undefined);
                return undefined;
            }
            const data = (await res.json()) as { is_ai_generated?: boolean };
            const flag = data.is_ai_generated === true;
            cache.set(mediaId, flag);
            return flag;
        } catch {
            cache.set(mediaId, undefined);
            return undefined;
        } finally {
            inflight.delete(mediaId);
        }
    })();
    inflight.set(mediaId, p);
    return p;
}

/**
 * Liefert für jede übergebene Media-ID einen booleschen `is_ai_generated`-
 * Zustand. `undefined`-IDs werden ignoriert. Rückgabe ist ein Record
 * `{ [mediaId]: true | false }`; fehlende Schlüssel bedeuten „unbekannt".
 */
export function useMediaAiFlags(mediaIds: Array<string | undefined>): Record<string, boolean> {
    const key = mediaIds.filter(Boolean).sort().join("|");
    const [flags, setFlags] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        for (const id of mediaIds) {
            if (id && cache.has(id)) {
                const v = cache.get(id);
                if (typeof v === "boolean") initial[id] = v;
            }
        }
        return initial;
    });

    useEffect(() => {
        let cancelled = false;
        // Bei Cache-Updates von außen (setAiFlagLocal) auch re-rendern.
        const bump = () => {
            if (cancelled) return;
            setFlags((prev) => {
                const next = { ...prev };
                let changed = false;
                for (const id of mediaIds) {
                    if (!id) continue;
                    const v = cache.get(id);
                    if (typeof v === "boolean" && next[id] !== v) {
                        next[id] = v;
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        };
        subscribers.add(bump);
        const ids = mediaIds.filter((x): x is string => typeof x === "string" && x.length > 0);
        if (ids.length > 0) {
            void Promise.all(ids.map((id) => loadOne(id))).then((results) => {
                if (cancelled) return;
                setFlags((prev) => {
                    const next = { ...prev };
                    let changed = false;
                    ids.forEach((id, i) => {
                        const v = results[i];
                        if (typeof v === "boolean" && next[id] !== v) {
                            next[id] = v;
                            changed = true;
                        }
                    });
                    return changed ? next : prev;
                });
            });
        }
        return () => {
            cancelled = true;
            subscribers.delete(bump);
        };
    }, [key]);

    return flags;
}
